import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import ReactSelect from 'react-select';
import arrowTop from '~/Assets/Images/arrow-top.png';
import arrowBottom from '~/Assets/Images/arrow-bottom.png';

import {
  Container,
  PositionCircle,
  DropdownWrapper,
  PercentageWrapper,
  PercentageInput,
  ActionButtonsWrapper,
  RemoveButton,
  AddButton,
} from './styles';

function SelectCampaignType({
  position: { position },
  item,
  onChangeItem,
  availableCampaignTypes,
  canRemoveItem,
  onAddNewItem,
  onRemoveItem,
  selectedItem,
  setSelectedItem,
  isEditable = true,
  onSwapPosition,
  canMoveUp,
  canMoveDown,
}) {

  const formatPercentage = (value) => {
    if (!value) return 100;

    // Check if the original value has more than 2 decimal places BEFORE multiplication
    const decimalPart = value.toString().split('.')[1];
    if (decimalPart && decimalPart.length > 2) {
      // If more than 2 decimals, limit to 2 and convert to percentage
      return parseFloat((value * 100).toFixed(2));
    }

    // Otherwise, convert to percentage and fix floating point precision
    return parseFloat((value * 100).toFixed(0));
  };

  const [percentage, setPercentage] = useState(formatPercentage(item.percentage));

  // Update local state when item.percentage changes externally
  React.useEffect(() => {
    setPercentage(formatPercentage(item.percentage));
  }, [item.percentage]);

  return (
    <Container isSelected={selectedItem === item} onClick={() => setSelectedItem(item)}>
      {/* Position Circle with Swap Arrows */}
      <PositionCircle>
        {/* Up Arrow */}
        {canMoveUp && isEditable && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSwapPosition(position, 'up');
            }}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '2px',
              position: 'absolute',
              top: '-16px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1,
              width: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title={`Trocar com posição ${position - 1}`}
          >
            <img
              src={arrowTop}
              alt="Move up"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
            />
          </button>
        )}

        {/* Position Number */}
        <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
          {position}
        </div>

        {/* Down Arrow */}
        {canMoveDown && isEditable && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSwapPosition(position, 'down');
            }}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '2px',
              position: 'absolute',
              bottom: '-16px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1,
              width: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title={`Trocar com posição ${position + 1}`}
          >
            <img
              src={arrowBottom}
              alt="Move down"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
            />
          </button>
        )}
      </PositionCircle>

      {/* Campaign Type Dropdown */}
      <DropdownWrapper >
        {item.type === 'PROBE' && item.isAbTest ? (
          <div style={{
            minHeight: '36px',
            border: '1px solid #bbdefb',
            borderRadius: '6px',
            fontSize: '14px',
            padding: '8px 12px',
            backgroundColor: '#e3f2fd',
            color: '#007bff',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center'
          }}>
            PROBE (Auto-gerado)
          </div>
        ) : (
          <ReactSelect
            options={availableCampaignTypes.filter(option => !option.disabled)} 
            value={availableCampaignTypes.find(option => option.value === item.type)}
            onChange={(selectedOption) => onChangeItem({ type: selectedOption && selectedOption.value })}
            placeholder="Selecione o tipo"
            isClearable={!item.isAbTest && isEditable}
            isDisabled={item.isAbTest || !isEditable}
            styles={{
              control: (provided) => ({
                ...provided,
                minHeight: '36px',
                border: '1px solid #ced4da',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: item.isAbTest ? '#f8f9fa' : provided.backgroundColor,
                '&:hover': {
                  borderColor: '#007bff',
                },
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected ? '#007bff' : 'white',
                color: state.isSelected ? 'white' : '#212529',
                fontSize: '14px',
                '&:hover': {
                  backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa',
                },
              }),
            }}
          />
        )}
      </DropdownWrapper>

      {/* Percentage Input */}
      {item.type && (
        <PercentageWrapper>
          <PercentageInput
            type="number"
            value={percentage}
            onChange={(e) => {
              const value = parseFloat(e.target.value);
              if (!isNaN(value)) {
                setPercentage(Math.max(0.01, Math.min(100, value)));
              }
            }}
            onBlur={(e) => {
              const value = parseFloat(percentage);
              if (!isNaN(value)) {
                const clampedValue = Math.max(0.01, Math.min(100, value));
                const formattedValue = clampedValue % 1 === 0 ? clampedValue : parseFloat(clampedValue.toFixed(2));
                onChangeItem({ percentage: formattedValue / 100 });
                setPercentage(formattedValue);
              }
            }}
            onFocus={(e) => {
              e.target.select();
            }}
            disabled={!isEditable}
          />
        </PercentageWrapper>
      )}

      {/* Action Buttons - Only show when editable */}
      {isEditable && (
        <ActionButtonsWrapper>
          {canRemoveItem && (
            <RemoveButton
              onClick={e => {
                e.stopPropagation();
                onRemoveItem();
              }}
            >
              ×
            </RemoveButton>
          )}
          {availableCampaignTypes.length > 0 && (
            <AddButton
              onClick={e => {
                e.stopPropagation();
                onAddNewItem();
              }}
            >
              +
            </AddButton>
          )}
        </ActionButtonsWrapper>
      )}
    </Container>
  );
}

const mapStateToProps = ({
}) => ({
});

const mapDispatchToProps = dispatch => bindActionCreators(
  {
  },
  dispatch,
);

SelectCampaignType.propTypes = {
  position: PropTypes.object,
  item: PropTypes.object,
  onChangeItem: PropTypes.func,
  availableCampaignTypes: PropTypes.array,
  onAddNewItem: PropTypes.func,
  onRemoveItem: PropTypes.func,
  canRemoveItem: PropTypes.bool,
  setSelectedItem: PropTypes.func,
  selectedItem: PropTypes.object,
  isEditable: PropTypes.bool,
  onSwapPosition: PropTypes.func,
  canMoveUp: PropTypes.bool,
  canMoveDown: PropTypes.bool,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SelectCampaignType);